import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Modal, Spin } from "antd";
import { NotificationManager } from "react-notifications";
import PurchaseOrderServices from "../../services/purchaseOrder.service";
import PoLineItemsTable from "./shared/PoLineItemsTable";
import "../../assets/css/po2-design.css";

// Task 1902/1903 (Feature 1861 / F3): one screen serves both the retailer's review (locked
// pricing, qty/remove editable, Approve/Return/Cancel actions) and the general order
// detail/history view (read-only lines, timeline, commit-receipt styling) - which mode
// applies is derived from the caller's role vs. the order's current status, not a separate
// route. This mirrors the approved prototype's own vDetail()/rReview() relationship - they
// share almost everything, only the action set and line-editability differ.
//
// Epic 1857 UI-redesign (Phase 3): restyled to the prototype's vDetail()/rReview() layout.
// primary/terminal flags added to TRANSITIONS (display-only, matching the prototype's own
// table) so Send/Approve/Accept/Reissue render as the one primary button and Cash and Carry
// renders neutral-navy instead of red - the backend (PoLifecycle.cs) is still the real
// source of truth and 409s on anything illegal regardless of what this table says.
const TRANSITIONS = {
  DRAFT: [
    { event: "SEND", actor: "VENDOR", label: "Send to Retailer", primary: true },
    { event: "CONFIRM_BEHALF", actor: "VENDOR", label: "Confirm on Their Behalf", needsName: true },
    { event: "INVOICE", actor: "VENDOR", label: "Mark as Invoice", confirm: true },
    { event: "CASH_CARRY", actor: "VENDOR", label: "Cash and Carry", confirm: true, terminal: true },
  ],
  PENDING_RETAILER: [
    { event: "APPROVE", actor: "RETAILER", label: "Approve as Written", primary: true },
    { event: "EDIT_RETURN", actor: "RETAILER", label: "Return with My Changes" },
    { event: "CONFIRM_BEHALF", actor: "VENDOR", label: "Confirm on Their Behalf", needsName: true },
    { event: "CANCEL", actor: "PARTY", label: "Cancel Order", confirm: true, danger: true },
  ],
  PENDING_VENDOR: [
    { event: "ACCEPT", actor: "VENDOR", label: "Accept Their Changes", primary: true },
    { event: "AMEND", actor: "VENDOR", label: "Amend and Resend" },
    { event: "CANCEL", actor: "PARTY", label: "Cancel Order", confirm: true, danger: true },
  ],
  PO: [
    { event: "INVOICE", actor: "VENDOR", label: "Mark as Invoice", confirm: true },
    { event: "CASH_CARRY", actor: "VENDOR", label: "Cash and Carry", confirm: true, terminal: true },
    { event: "CANCEL", actor: "PARTY", label: "Cancel Order", confirm: true, danger: true },
  ],
  INVOICE: [{ event: "DISPUTE", actor: "RETAILER", label: "Dispute This Invoice", needsReason: true, danger: true }],
  DISPUTED: [{ event: "REISSUE", actor: "VENDOR", label: "Resolve and Reissue", primary: true }],
  CASH_CARRY: [],
  CANCELLED: [],
};

const STATUS_LABELS = {
  DRAFT: "Draft",
  PENDING_RETAILER: "Pending Retailer Confirmation",
  PENDING_VENDOR: "Pending Vendor Review",
  PO: "Purchase Order",
  INVOICE: "Invoice",
  DISPUTED: "Disputed",
  CASH_CARRY: "Invoice, Cash and Carry",
  CANCELLED: "Cancelled",
};

const EVENT_LABELS = {
  SEND: "Sent to retailer",
  APPROVE: "Approved as written",
  EDIT_RETURN: "Edited and returned",
  ACCEPT: "Changes accepted",
  AMEND: "Amended and resent",
  CONFIRM_BEHALF: "Confirmed on retailer's behalf",
  INVOICE: "Marked as invoice",
  CASH_CARRY: "Cash and carry",
  DISPUTE: "Disputed",
  REISSUE: "Resolved and reissued",
  CANCEL: "Cancelled",
};

const OrderDetail = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const { poId } = useParams();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;
  const memberType = loginDetials?.loginDetials?.responseData?.dealerMemberTypeName;

  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const [nameModal, setNameModal] = useState(null); // pending event needing confirmedByName
  const [confirmedByName, setConfirmedByName] = useState("");
  const [reasonModal, setReasonModal] = useState(null); // pending event needing a reason
  const [disputeReason, setDisputeReason] = useState("");
  // Restates buyer + exact amount before a confirm:true action commits (spec's own named
  // hard requirement) - same pattern as VendorOrderBuilder.js's close-order confirm.
  const [closeConfirm, setCloseConfirm] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      PurchaseOrderServices.GetOrderDetail(poId),
      PurchaseOrderServices.GetOrderHistory(poId),
    ])
      .then(([orderRes, historyRes]) => {
        setLoading(false);
        if (orderRes?.data?.status) setOrder(orderRes.data.responseData);
        if (historyRes?.data?.status) setHistory(historyRes.data.responseData || []);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    if (poId) load();
  }, [poId]);

  if (!order) {
    return (
      <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
        <Spin spinning={loading} />
      </div>
    );
  }

  const isVendor = memberType === "Vendor" || memberType === "VendorDiamond" || memberType === "Combined";
  const isRetailer = memberType === "Retailer";
  const myActorRole = isVendor && order.vendorId === dealerId ? "VENDOR" : isRetailer && order.dealerId === dealerId ? "RETAILER" : null;

  const availableActions = (TRANSITIONS[order.statusCode] || []).filter(
    (a) => a.actor === myActorRole || (a.actor === "PARTY" && myActorRole)
  );

  const isReviewMode = myActorRole === "RETAILER" && order.statusCode === "PENDING_RETAILER";
  const committedConfirmedByBehalf = order.statusCode === "PO" && history.some((h) => h.eventType === "CONFIRM_BEHALF");

  const doTransition = (event, extra) => {
    setLoading(true);
    PurchaseOrderServices.Transition({ poId, event, ...extra })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          NotificationManager.success(t("Order updated."));
          load();
        } else {
          NotificationManager.error(t("That action isn't allowed right now."));
        }
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("That action isn't allowed right now."));
      });
  };

  // Task 1904/1905 (Feature 1863 / F5, Epic 1857 UI-redesign Phase 8): always regenerates
  // rather than reusing a stored document, so the preview reflects the order's current
  // state - matches VendorOrderBuilder.js's same choice.
  const documentTypeForStatus = (status) => {
    if (status === "PO") return "PURCHASE_ORDER";
    if (status === "INVOICE" || status === "DISPUTED") return "INVOICE";
    if (status === "CASH_CARRY") return "CASH_CARRY";
    return "PROPOSAL";
  };
  const handlePreviewDocument = () => {
    setLoading(true);
    PurchaseOrderServices.GenerateOrderDocument(poId, documentTypeForStatus(order.statusCode))
      .then((res) => {
        setLoading(false);
        if (res?.data?.status && res.data.responseData?.documentUrl) {
          window.open(res.data.responseData.documentUrl, "_blank", "noopener");
        } else {
          NotificationManager.error(t("Could not generate the document."));
        }
      })
      .catch(() => {
        setLoading(false);
        NotificationManager.error(t("Could not generate the document."));
      });
  };

  const handleAction = (action) => {
    if (action.needsName) {
      setNameModal(action.event);
      return;
    }
    if (action.needsReason) {
      setReasonModal(action.event);
      return;
    }
    if (action.confirm) {
      setCloseConfirm(action);
      return;
    }
    doTransition(action.event);
  };

  const handleQtyChange = (lineId, quantity) => {
    setLoading(true);
    PurchaseOrderServices.UpdateOrderLineQuantity({ poId, lineId, quantity })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) load();
      })
      .catch(() => setLoading(false));
  };

  // Deliberately NO undo action here (unlike VendorOrderBuilder.js's remove) - this path
  // only runs in retailer review mode, and PoLineBuilder attributes an ad hoc line's
  // VendorId to whoever calls UpsertOrderLine (PoLineBuilder.cs:105). A retailer "undoing"
  // a remove would silently create the line under their own DealerId instead of the real
  // vendor's - a plain confirmation is the safe option until that backend gap is fixed.
  const handleRemoveLine = (lineId) => {
    setLoading(true);
    PurchaseOrderServices.RemoveOrderLine({ poId, lineId })
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          NotificationManager.success(t("Line removed."));
          load();
        }
      })
      .catch(() => setLoading(false));
  };

  return (
    <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
      <div className="po2-phead">
        <div>
          <h1>
            {t("Order")} #{order.poId}
          </h1>
          <div className="po2-desc">
            {order.contactFullName && (
              <>
                {order.contactFullName}
                {order.contactBusinessName ? ` (${order.contactBusinessName})` : ""}
                {order.recipientType === "INDIVIDUAL" && <span className="po2-tagpur" style={{ marginInlineStart: 6 }}>{t("Individual")}</span>}
                {!order.retailerDealerId && <span className="po2-tagwarn" style={{ marginInlineStart: 6 }}>{t("Not yet in JewelCloud")}</span>}
              </>
            )}
            {order.referenceNumber ? ` · ${t("Reference")}: ${order.referenceNumber}` : ""}
          </div>
        </div>
        <div className="po2-right">
          <span className={`po2-st po2-st-${order.statusCode}`}>{t(STATUS_LABELS[order.statusCode] || order.statusCode)}</span>
          <button className="po2-btn po2-neutral po2-sm" onClick={() => navigate(-1)}>
            {t("← Back")}
          </button>
        </div>
      </div>

      <Spin spinning={loading}>
        <div className="po2-grid po2-rail-grid">
          <div>
            {order.statusCode === "CASH_CARRY" && (
              <div className="po2-receipt po2-term">
                <div className="po2-t">🔒 {t("Cash and carry - terminal")}</div>
                <div className="po2-a">${order.orderTotal?.toFixed(2)}</div>
                <div className="po2-sub">{t("No edit, reject or dispute action is offered to the retailer.")}</div>
              </div>
            )}
            {committedConfirmedByBehalf && (
              <div className="po2-receipt">
                <div className="po2-t">✓ {t("Committed purchase order")}</div>
                <div className="po2-a">${order.orderTotal?.toFixed(2)}</div>
                <div className="po2-sub">
                  {t("Confirmed on the retailer's behalf. The retailer was notified and retains the right to dispute.")}
                </div>
              </div>
            )}

            {isReviewMode && (
              <div className="po2-insight po2-lock">
                <b>{t("What you can and cannot change")}</b>
                {t("Editable - quantities, and removing lines. Locked - unit prices, discounts and payment terms.")}
              </div>
            )}

            <div className="po2-panel po2-flush">
              <div className="po2-ph">
                <h3>{t("Line items")}</h3>
                <span className="po2-desc">{order.lines?.length || 0} {t("lines")}</span>
              </div>
              <PoLineItemsTable
                lines={order.lines}
                qtyEditable={isReviewMode}
                discountEditable={false}
                showRemove={isReviewMode}
                onQtyChange={handleQtyChange}
                onRemove={handleRemoveLine}
              />
            </div>

            <div className="po2-panel">
              <div className="po2-ph">
                <h3>{t("History")}</h3>
                <span className="po2-desc">{t("Immutable - actor, timestamp and a snapshot at every change")}</span>
              </div>
              <div className="po2-hist">
                {[...history].reverse().map((ev) => (
                  <div className="po2-ev" key={ev.orderEventID}>
                    <b>{t(EVENT_LABELS[ev.eventType] || ev.eventType)}</b>
                    <span>
                      {ev.actorRole?.toLowerCase()} · {ev.occurredUtc ? new Date(ev.occurredUtc).toLocaleString() : ""}
                      {ev.detail ? ` · ${ev.detail}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="po2-rail">
            <div className="po2-panel">
              <div className="po2-ph">
                <h3>{t("Summary")}</h3>
              </div>
              {isReviewMode ? (
                <div className="po2-insight po2-lock" style={{ marginBottom: 12 }}>
                  <b>{t("Set by the vendor")} 🔒</b>
                  {t("Terms")}: {order.termsCode || "-"} · {t("Order Discount")}: {order.orderDiscountPct}% ·{" "}
                  {t("Early Pay")}: {order.earlyPayPercent}% / {order.earlyPayDays} {t("days")}
                </div>
              ) : (
                <div className="po2-sub" style={{ marginBottom: 10 }}>
                  {t("Terms")}: {order.termsCode || "-"} &nbsp;·&nbsp; {t("Reference")}: {order.referenceNumber || "-"}
                </div>
              )}
              <div className="po2-tot">
                <span>{t("Subtotal")}</span>
                <span className="po2-v">${order.subtotalAmount?.toFixed(2)}</span>
              </div>
              <div className="po2-tot">
                <span>{t("Discount")}</span>
                <span className="po2-v po2-neg">-${order.discountTotal?.toFixed(2)}</span>
              </div>
              <div className="po2-grandbox">
                <div className="po2-lab">{t("Total")}</div>
                <div className="po2-amt">${order.orderTotal?.toFixed(2)}</div>
              </div>
            </div>

            <div className="po2-panel" style={{ marginBottom: 0 }}>
              <div className="po2-ph">
                <h3>{isReviewMode ? t("Your decision") : t("Actions")}</h3>
                {!isReviewMode && myActorRole && <span className="po2-desc">{t("as")} {myActorRole.toLowerCase()}</span>}
              </div>
              {availableActions.length > 0 ? (
                availableActions.map((a) =>
                  a.primary ? (
                    <button key={a.event} className="po2-btn po2-full" style={{ marginBottom: 8 }} onClick={() => handleAction(a)}>
                      {t(a.label)}
                    </button>
                  ) : (
                    <button key={a.event} className={`po2-actionrow ${a.terminal ? "po2-term" : ""}`} onClick={() => handleAction(a)}>
                      <b style={a.danger ? { color: "var(--po2-bad)" } : undefined}>
                        {t(a.label)} {a.terminal ? "🔒" : ""}
                      </b>
                    </button>
                  )
                )
              ) : (
                <div className="po2-hint">
                  {["CASH_CARRY", "CANCELLED"].includes(order.statusCode)
                    ? t("Terminal state. No action is available to anyone.")
                    : t("No action available to you right now.")}
                </div>
              )}
              <button className="po2-btn po2-neutral po2-sm po2-full" style={{ marginTop: 8 }} onClick={handlePreviewDocument}>
                {t("View document")}
              </button>
            </div>
          </div>
        </div>
      </Spin>

      <Modal
        title={t("Confirm on Their Behalf")}
        visible={!!nameModal}
        onCancel={() => setNameModal(null)}
        onOk={() => {
          if (!confirmedByName) {
            NotificationManager.error(t("A name is required."));
            return;
          }
          const ev = nameModal;
          setNameModal(null);
          doTransition(ev, { confirmedByName });
        }}
      >
        <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
          <div className="po2-field">
            <label>{t("Confirmed By")}</label>
            <input className="po2-in" value={confirmedByName} onChange={(e) => setConfirmedByName(e.target.value)} />
          </div>
        </div>
      </Modal>

      <Modal
        title={t("Dispute Reason")}
        visible={!!reasonModal}
        onCancel={() => setReasonModal(null)}
        onOk={() => {
          if (!disputeReason) {
            NotificationManager.error(t("A reason is required."));
            return;
          }
          const ev = reasonModal;
          setReasonModal(null);
          doTransition(ev, { disputeReason });
        }}
      >
        <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
          <div className="po2-field">
            <label>{t("Reason")}</label>
            <Input.TextArea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} />
          </div>
        </div>
      </Modal>

      <Modal
        title={closeConfirm ? t(closeConfirm.label) : ""}
        visible={!!closeConfirm}
        onCancel={() => setCloseConfirm(null)}
        onOk={() => {
          const action = closeConfirm;
          setCloseConfirm(null);
          doTransition(action.event);
        }}
        okText={closeConfirm?.terminal ? `${t("Yes - paid")} $${order.orderTotal?.toFixed(2)} ${t("and collected")}` : t("Confirm")}
      >
        {closeConfirm && (
          <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"} style={{ padding: 0, background: "transparent", border: "none" }}>
            <div className={`po2-insight ${closeConfirm.terminal ? "po2-warn" : ""}`}>
              <b>{closeConfirm.terminal ? t("This cannot be undone") : t("Please confirm")}</b>
            </div>
            <div style={{ display: "flex", gap: 28 }}>
              <div>
                <div className="po2-field"><label>{t("Buyer")}</label></div>
                <b style={{ fontSize: 15 }}>{order.contactFullName || "-"}</b>
              </div>
              <div>
                <div className="po2-field"><label>{t("Amount")}</label></div>
                <b style={{ fontSize: 22, fontVariantNumeric: "tabular-nums" }}>${order.orderTotal?.toFixed(2)}</b>
                <div className="po2-sub">{order.lines?.length || 0} {t("lines")}</div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderDetail;
